import { useCallback, useMemo, useRef } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { commitLocalUpdate, ConnectionHandler, useRelayEnvironment } from 'react-relay';
import {
	type BoundActions,
	createActionsHook,
	createHook,
	createStore,
	defaultRegistry,
	type HookActionsFunction,
	type HookFunction,
	type StoreActionApi,
} from 'react-sweet-state';

import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { useRovoEntitlement } from '@atlassian/conversation-assistant-entitlement/controllers/rovo-entitlement';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import {
	type Agent,
	type AgentDetails,
	isRateLimitedError,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import {
	prefetchQuery,
	useQuery,
} from '@atlassian/conversation-assistant-service/common/utils/use-query';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import RelayDataID from '@atlassian/relay-data-id';

import { ROVO_AGENTS_FE_TEAM_NAME } from '../../common/constants/private.ts';
import { type BrowseCategory, type ClientAgent } from '../../common/types.ts';
import {
	DEFAULT_PAGE_INFO,
	fetchAgentByExternalConfigReference,
	fetchPaginatedAgents,
	type PageInfo,
} from '../../services/paginated-agents/index.ts';
import { getSuggestedOotbAgents } from '../../utils/agents/index.tsx';
import { useDeleteAgent } from '../agent-delete/index.tsx';
import { useAgentFavourites } from '../agent-favourite/index.tsx';
import { useAgentOverrider } from '../agent-overrider/index.tsx';
import { getOverridenAgents } from '../agent-overrider/utils.ts';
import { isHipaaError } from './constants.ts';
import messages from './messages.ts';

type CategorisedAgents = {
	public: Agent[];
	private: Agent[];
	system: Agent[];
};

const categoriseAgents = (agents: Agent[]): CategorisedAgents =>
	agents.reduce(
		(categorisedAgents: CategorisedAgents, agent: Agent) => {
			if (agent.creator_type === 'SYSTEM') {
				categorisedAgents.system.push(agent);
			} else if (agent.visibility === 'PUBLIC') {
				categorisedAgents.public.push(agent);
			} else if (agent.visibility === 'PRIVATE') {
				categorisedAgents.private.push(agent);
			} else if (!agent.visibility && agent.creator_type === 'CUSTOMER') {
				categorisedAgents.private.push(agent);
			}

			return categorisedAgents;
		},
		{
			public: [],
			private: [],
			system: [],
		},
	);

const connectionContainsNode = (
	connection: NonNullable<ReturnType<typeof ConnectionHandler.getConnection>>,
	nodeId: string,
): boolean =>
	(connection.getLinkedRecords('edges') ?? []).some(
		(edge) => edge?.getLinkedRecord('node')?.getDataID() === nodeId,
	);

const createInitialAgentsState = () => ({
	availableAgents: [] as Agent[],
	categorisedAgents: {
		private: [],
		public: [],
		system: [],
	} as CategorisedAgents,
	agentsInitialized: false,
	agentsError: undefined as ApiError | undefined,
});

/**
 * @private
 * @deprecated Please use `useAgentsPaginated...` hooks instead
 * @TODO move to different folder https://product-fabric.atlassian.net/browse/RAGE-1562
 */
const agentsStore = createStore({
	initialState: createInitialAgentsState(),
	name: 'AgentsStore',
	actions: {
		setAgents:
			(availableAgents: Agent[]) =>
			({ setState }) => {
				setState({
					availableAgents,
					categorisedAgents: categoriseAgents(availableAgents),
				});
			},

		setAgentsInitialized:
			() =>
			({ setState }) => {
				setState({
					agentsInitialized: true,
				});
			},

		setAgentsError:
			(agentsError: ApiError) =>
			({ setState }) => {
				setState({ agentsError });
			},

		resetAgentStore:
			() =>
			({ setState }: StoreActionApi<ReturnType<typeof createInitialAgentsState>>) => {
				setState(createInitialAgentsState());
			},
	},
});

/**
 * @private
 * @deprecated Please use `useAgentsPaginated...` instead
 * @TODO move to different folder https://product-fabric.atlassian.net/browse/RAGE-1562
 */
const useAgentsStore = createHook(agentsStore);

type UseAgentsListProps = {
	filter?: 'favourites';
} & AssistanceServiceInjectedParams;

type UseAgentsControllerProps = {
	onError?: (error: ApiError) => void;
} & AssistanceServiceInjectedParams;

/**
 * @private
 * @deprecated Internal to conversation-assistant packages. Outside consumers should use Relay to
 * fetch the data (Relay will manage caching across products). This API can change and break
 * external consumers.
 * Within this package, use paginated agent hooks instead.
 * @TODO move to different folder https://product-fabric.atlassian.net/browse/RAGE-1562
 */
export const useAgentsControllerInternal: (
	props: Omit<UseAgentsControllerProps, 'assistanceServiceInjectedParams'> &
		AssistanceServiceInputParam,
) => {
	availableAgents: Agent[];
	favouritedAgents: Agent[];
	categorisedAgents: CategorisedAgents;
	fetchAndStoreAvailableAgents: {
		data: void | null;
		error: ApiError | null;
		isFetching: boolean;
		refetch: () => Promise<void | null>;
	};
	deleteAgent: {
		data: void | null;
		error: ApiError | null;
		isSubmitting: boolean;
		mutate: (
			payload: string,
			callbacks?:
				| {
						onSuccess?: ((data: void) => void) | undefined;
						onError?: (error: ApiError) => void;
				  }
				| undefined,
		) => Promise<void | undefined>;
	};
	setFavouriteAgent: (id: string, isFavourite: boolean) => Promise<void | undefined>;
	defaultAgent: Agent | undefined;
	agentsInitialized: boolean;
	nonDefaultAgents: Agent[];
	nonDefaultFavouriteAgents: Agent[];
	updateAgentLocally: (agentId: string, updatedAgent: Partial<Agent>) => void;
	agentsError: ApiError | undefined;
} = createHookWithAssistanceService(
	({
		onError,
		assistanceServiceInjectedParams: {
			assistanceService,
			experienceTracker,
			experienceId,
			product,
			siteId,
			touchpointSource,
		},
	}: UseAgentsControllerProps) => {
		// TODO remove `isRovoEnabled`
		const { isRovoEnabled } = useRovoEntitlement();

		const { agentIdFiltersOverride, agentDefaultIdOverride } = useAgentOverrider();
		const [
			{ availableAgents, categorisedAgents, agentsInitialized, agentsError },
			{ setAgents, setAgentsInitialized, setAgentsError },
		] = useAgentsStore();
		const { formatMessage } = useIntl();

		const { favouriteAgent, unfavouriteAgent } = useAgentFavourites({
			assistanceServiceParams: {
				experienceId,
				product,
				siteId,
				touchpointSource,
			},
		});

		const favouritedAgents = availableAgents.filter((agent) => agent.favourite);
		const defaultAgent = useMemo(
			() => availableAgents.find((agent) => agent.is_default),
			[availableAgents],
		);
		const nonDefaultAgents = useMemo(
			() => availableAgents.filter((agent) => !agent.is_default),
			[availableAgents],
		);
		const nonDefaultFavouriteAgents = useMemo(
			() => nonDefaultAgents.filter((agent) => !agent.favourite),
			[nonDefaultAgents],
		);

		const fetchAndStoreAvailableAgents = useQuery({
			queryKey: fg('rovo_chat_site_switch_reset') ? ['fetchAgents', siteId] : ['fetchAgents'],
			queryFn: async () => {
				const isSiteSwitchResetEnabled = fg('rovo_chat_site_switch_reset');
				const generationAtRequestStart = getAgentStoresGeneration();

				try {
					experienceTracker.start({
						id: ExperienceName.FETCH_AGENTS,
						name: ExperienceName.FETCH_AGENTS,
					});

					const agents = await assistanceService.getAgents();

					if (isSiteSwitchResetEnabled && isStaleAgentStoresGeneration(generationAtRequestStart)) {
						experienceTracker.succeed({
							name: ExperienceName.FETCH_AGENTS,
						});
						return;
					}

					const transformedAgents = agents.map((agent) => {
						// Rename default agent to Chat, which is displayed in the UI
						if (agent.is_default) {
							agent.name = formatMessage(messages.defaultAgentName);
							agent.description = formatMessage(messages.defaultAgentDescription);
						}

						return agent;
					});

					const overriddenAgents = getOverridenAgents(
						transformedAgents,
						agentIdFiltersOverride,
						agentDefaultIdOverride,
					);

					setAgents(overriddenAgents);

					experienceTracker.succeed({
						name: ExperienceName.FETCH_AGENTS,
					});

					setAgentsInitialized();
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
				} catch (error: any) {
					//Filter out HIPAA errors from experience tracker https://product-fabric.atlassian.net/browse/ROVOFE-543
					if (isHipaaError(error)) {
						experienceTracker.succeed({
							name: ExperienceName.FETCH_AGENTS,
						});
						return;
					}

					const isStaleSite =
						isSiteSwitchResetEnabled && isStaleAgentStoresGeneration(generationAtRequestStart);

					if (isRateLimitedError(error) && !isStaleSite) {
						setAgentsError(error);
					}

					experienceTracker.fail({ name: ExperienceName.FETCH_AGENTS, error });
					throw error;
				}
			},
			onError,
			enabled: isRovoEnabled,
			refetchOnWindowFocus: false,
		});

		const { deleteAgent } = useDeleteAgent({
			onSuccess: () => {
				fetchAndStoreAvailableAgents.refetch();
			},
			assistanceServiceParams: {
				experienceId,
				product,
				siteId,
				touchpointSource,
			},
			onInit: (id) => {
				setAgents(availableAgents.filter((agent) => agent.id !== id));
			},
			onError: () => {
				setAgents(availableAgents);
			},
		});

		const setFavouriteAgent = useCallback(
			async (id: string, isFavourite: boolean) => {
				const updatedAgent = availableAgents.find((a) => a.id === id);
				const setUpdatedAgent = (shouldBeFavourite: boolean) => {
					if (updatedAgent) {
						updatedAgent.favourite = shouldBeFavourite;
						setAgents(
							availableAgents.map((a) => {
								// If the current agent's id matches the new agent's id, return the new agent.
								// Otherwise, return the current agent.
								return a.id === updatedAgent.id ? updatedAgent : a;
							}),
						);
					}
				};

				//Optimistically update favourite value before we mutate
				setUpdatedAgent(isFavourite);

				//Pass callback to roll back optimistic update if mutation fails
				const errorCallback = {
					onError: () => {
						setUpdatedAgent(!isFavourite);
					},
				};
				if (isFavourite) {
					return await favouriteAgent.mutate(id, errorCallback);
				} else {
					return await unfavouriteAgent.mutate(id, errorCallback);
				}
			},
			[availableAgents, setAgents, favouriteAgent, unfavouriteAgent],
		);
		/** This updates the agent on the client side only
		 * Useful for updating the agent after a successful mutation
		 */
		const updateAgentLocally = useCallback(
			(agentId: string, updatedAgent: Partial<Agent>) => {
				const updatedAgents = availableAgents.map((agent) => {
					if (agent.id === agentId) {
						return {
							...agent,
							...updatedAgent,
						};
					}
					return agent;
				});

				setAgents(updatedAgents);
			},
			[availableAgents, setAgents],
		);

		return {
			availableAgents,
			favouritedAgents,
			categorisedAgents,
			fetchAndStoreAvailableAgents,
			deleteAgent,
			setFavouriteAgent,
			defaultAgent,
			agentsInitialized,
			nonDefaultAgents,
			nonDefaultFavouriteAgents,
			updateAgentLocally,
			agentsError,
		};
	},
);

type StoreAgentCategory = {
	agentIds: string[];
	pageInfo: PageInfo;
	abortController: AbortController;
	agentsError?: Error | undefined;
};
type StoreAgentCategories = Record<BrowseCategory, StoreAgentCategory>;

const createEmptyAgentCategory = (): StoreAgentCategory => ({
	agentIds: [],
	pageInfo: { ...DEFAULT_PAGE_INFO },
	abortController: new AbortController(),
});

const agentsPaginatedStore = createStore({
	initialState: {
		/*
		Hash map which is the source of truth for agent data
		agent categories simply hold the agent ids to perform a lookup
		*/
		agentsMap: {} as Record<string, ClientAgent>,
		loadingCategories: new Set<BrowseCategory>(),
		agentCategories: {
			['all-agents']: {
				agentIds: [] as string[],
				pageInfo: DEFAULT_PAGE_INFO,
				abortController: new AbortController(),
			},
			['starred-agents']: {
				agentIds: [] as string[],
				pageInfo: DEFAULT_PAGE_INFO,
				abortController: new AbortController(),
			},
			['my-agents']: {
				agentIds: [] as string[],
				pageInfo: DEFAULT_PAGE_INFO,
				abortController: new AbortController(),
			},
			['templates']: {
				agentIds: [] as string[],
				pageInfo: DEFAULT_PAGE_INFO,
				abortController: new AbortController(),
			},
			['verified-agents']: {
				agentIds: [] as string[],
				pageInfo: DEFAULT_PAGE_INFO,
				abortController: new AbortController(),
			},
			['available-for-jira']: {
				agentIds: [] as string[],
				pageInfo: DEFAULT_PAGE_INFO,
				abortController: new AbortController(),
			},
		} as StoreAgentCategories,
	},
	name: 'AgentsPaginatedStore',
	actions: {
		/**
		 * This action populates a specific category with backend data
		 * it will store the ids in `agentCategories` and the agents in `agentsMap`
		 */
		populateAgentCategories:
			({
				agentsList,
				pageInfo,
				category = 'all-agents',
			}: {
				agentsList: Agent[];
				pageInfo: PageInfo;
				category: BrowseCategory;
			}): (({
				setState,
				getState,
			}: StoreActionApi<{
				/*
                    Hash map which is the source of truth for agent data
                    agent categories simply hold the agent ids to perform a lookup
                    */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => void) =>
			({ setState, getState }) => {
				const { agentsMap, agentCategories } = getState();
				const updatedAgentsMap = { ...agentsMap };
				const updatedAgentCategories = { ...agentCategories };

				updatedAgentCategories[category].pageInfo = pageInfo;

				if (!agentsList.length) {
					updatedAgentCategories[category].agentIds = [];
				} else {
					agentsList.forEach((agent) => {
						// Ensure isPlaceholder is cleared when hydrating with real BE data
						const { isPlaceholder: _isPlaceholder, ...existingData } =
							updatedAgentsMap[agent.id] ?? {};
						updatedAgentsMap[agent.id] = { ...existingData, ...agent };
						updatedAgentCategories[category].agentIds.push(agent.id);
					});
				}

				setState({
					agentsMap: updatedAgentsMap,
					agentCategories: updatedAgentCategories,
				});
			},

		setAgentCategoriesByCategory:
			({
				categoryName,
				agentCategoryData,
			}: {
				categoryName: BrowseCategory;
				agentCategoryData: StoreAgentCategory;
			}): (({
				getState,
				setState,
			}: StoreActionApi<{
				/*
                    Hash map which is the source of truth for agent data
                    agent categories simply hold the agent ids to perform a lookup
                    */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => void) =>
			({ getState, setState }) => {
				const { agentCategories } = getState();

				const updatedAgentCategories = {
					...agentCategories,
					[categoryName]: agentCategoryData,
				};

				setState({ agentCategories: updatedAgentCategories });
			},

		setCategoryError:
			(
				categoryName: BrowseCategory,
				agentsError: Error | undefined,
			): (({
				getState,
				setState,
			}: StoreActionApi<{
				/*
                Hash map which is the source of truth for agent data
                agent categories simply hold the agent ids to perform a lookup
                */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => void) =>
			({ getState, setState }) => {
				const { agentCategories } = getState();
				const updatedAgentCategories = {
					...agentCategories,
					[categoryName]: {
						...agentCategories[categoryName],
						agentsError,
					},
				};

				setState({ agentCategories: updatedAgentCategories });
			},

		getAgentCategoriesByCategory:
			(
				category: BrowseCategory,
			): (({
				getState,
			}: StoreActionApi<{
				/*
                Hash map which is the source of truth for agent data
                agent categories simply hold the agent ids to perform a lookup
                */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => StoreAgentCategory) =>
			({ getState }) => {
				const state = getState();
				const { agentCategories } = state;
				return agentCategories[category];
			},

		removeAgent:
			(
				id: string,
			): (({
				setState,
				getState,
			}: StoreActionApi<{
				/*
                Hash map which is the source of truth for agent data
                agent categories simply hold the agent ids to perform a lookup
                */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => {
				agent: Agent | undefined;
				categories: BrowseCategory[];
			}) =>
			({ setState, getState }) => {
				const { agentsMap, agentCategories } = getState();
				const catsImpacted: Set<BrowseCategory> = new Set();
				let agentToReturn: Agent | undefined;

				const updatedAgentsMap = { ...agentsMap };
				const updatedAgentCategories = { ...agentCategories };

				if (agentsMap[id]) {
					agentToReturn = agentsMap[id];
					delete updatedAgentsMap[id];
				}

				(Object.keys(agentCategories) as BrowseCategory[]).forEach((category) => {
					if (updatedAgentCategories[category]?.agentIds.includes(id)) {
						updatedAgentCategories[category].agentIds = updatedAgentCategories[
							category
						].agentIds.filter((agentId: string) => agentId !== id);
						catsImpacted.add(category);
					}
				});

				setState({ agentsMap: updatedAgentsMap, agentCategories: updatedAgentCategories });

				return {
					agent: agentToReturn,
					categories: Array.from(catsImpacted),
				};
			},

		addAgent:
			(
				agent: Agent,
				categories: BrowseCategory[] = ['all-agents'],
			): (({
				setState,
				getState,
			}: StoreActionApi<{
				/*
                Hash map which is the source of truth for agent data
                agent categories simply hold the agent ids to perform a lookup
                */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => void) =>
			({ setState, getState }) => {
				const { agentsMap, agentCategories } = getState();
				const updatedAgentsMap = { ...agentsMap };
				const updatedAgentCategories = { ...agentCategories };

				categories.forEach((category) => {
					updatedAgentCategories?.[category]?.agentIds.push(agent.id);
				});

				// Ensure isPlaceholder is cleared when adding full agent data
				const { isPlaceholder: _isPlaceholder, ...existingData } = updatedAgentsMap[agent.id] ?? {};
				updatedAgentsMap[agent.id] = { ...existingData, ...agent };

				setState({ agentsMap: updatedAgentsMap, agentCategories: updatedAgentCategories });
			},

		updateAgent:
			(
				id: string,
				data: Partial<Agent>,
			): (({
				setState,
				getState,
			}: StoreActionApi<{
				/*
                Hash map which is the source of truth for agent data
                agent categories simply hold the agent ids to perform a lookup
                */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => Agent) =>
			({ setState, getState }) => {
				const { agentsMap } = getState();
				const updatedAgentsMap = { ...agentsMap };
				const prevData = agentsMap[id] ?? {};
				const updatedData = { ...prevData, ...data };

				// Clean up placeholder flag when real agent data arrives
				// Keep placeholderAvatarUrl to avoid avatar flicker (same image, stable source)
				if (prevData.isPlaceholder) {
					delete updatedData.isPlaceholder;
				}

				updatedAgentsMap[id] = updatedData;

				setState({ agentsMap: updatedAgentsMap });

				return updatedData;
			},

		setCategoryLoading:
			(
				category: BrowseCategory,
				loading: boolean,
			): (({
				setState,
				getState,
			}: StoreActionApi<{
				/*
                Hash map which is the source of truth for agent data
                agent categories simply hold the agent ids to perform a lookup
                */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => void) =>
			({ setState, getState }) => {
				const { loadingCategories } = getState();
				const updatedLoadingCategories = new Set(loadingCategories);

				if (loading) {
					updatedLoadingCategories.add(category);
				} else {
					updatedLoadingCategories.delete(category);
				}

				setState({ loadingCategories: updatedLoadingCategories });
			},

		abortCategoryFetch:
			(
				category: BrowseCategory,
			): (({
				getState,
			}: StoreActionApi<{
				/*
                Hash map which is the source of truth for agent data
                agent categories simply hold the agent ids to perform a lookup
                */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => void) =>
			({ getState }) => {
				const { agentCategories } = getState();
				agentCategories[category].abortController.abort();
				agentCategories[category].abortController = new AbortController();
			},

		/**
		 * Sets a placeholder agent in the agentsMap with partial data for immediate rendering.
		 * The agent is marked with `isPlaceholder: true` so that `useAgentsPaginatedByAgentId`
		 * still fetches full details from the network to replace it.
		 * Used for optimistic rendering of agent name/avatar from pubsub payloads.
		 */
		setPlaceholderAgent:
			(agentId: string, data: Partial<ClientAgent>) =>
			({
				setState,
				getState,
			}: StoreActionApi<{
				/*
				Hash map which is the source of truth for agent data
				agent categories simply hold the agent ids to perform a lookup
				*/
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => {
				const { agentsMap } = getState();
				// Only set placeholder if agent doesn't already exist with full data
				if (!agentsMap[agentId]) {
					const updatedAgentsMap = { ...agentsMap };
					updatedAgentsMap[agentId] = {
						...data,
						id: agentId,
						isPlaceholder: true,
					} as ClientAgent;
					setState({ agentsMap: updatedAgentsMap });
				}
			},

		getCachedAgentByExternalConfigReference:
			(
				externalConfigReference: string,
			): (({
				getState,
			}: StoreActionApi<{
				/*
                Hash map which is the source of truth for agent data
                agent categories simply hold the agent ids to perform a lookup
                */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => Agent | undefined) =>
			({ getState }) => {
				const { agentsMap } = getState();
				return Object.values(agentsMap).find(
					(agent) => agent.external_config_reference === externalConfigReference,
				);
			},

		resetPaginatedAgents:
			() =>
			({
				getState,
				setState,
			}: StoreActionApi<{
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => {
				const agentCategories = { ...getState().agentCategories };
				(Object.keys(agentCategories) as BrowseCategory[]).forEach((category) => {
					agentCategories[category].abortController.abort();
					agentCategories[category] = createEmptyAgentCategory();
				});

				setState({
					agentsMap: {},
					loadingCategories: new Set<BrowseCategory>(),
					agentCategories,
				});
			},
	},
});

/**
 * Both agent stores are page-level singletons, so switching site can only clear them, never replace
 * them. `agentStoresGeneration` is the version of the data they currently hold: async writers read
 * it before their request and compare afterwards, so a response belonging to the site we just left
 * is dropped instead of repopulating the cleared store.
 */
let agentStoresGeneration = 0;

const getAgentStoresGeneration = (): number => agentStoresGeneration;

const isStaleAgentStoresGeneration = (generation: number): boolean =>
	generation !== agentStoresGeneration;

/**
 * @private
 */
export const resetAgentStores = (): void => {
	agentStoresGeneration += 1;
	defaultRegistry.getStore(agentsStore).actions.resetAgentStore();
	defaultRegistry.getStore(agentsPaginatedStore).actions.resetPaginatedAgents();
};

// exported for testing
/**
 * @private
 * @deprecated Internal to conversation-assistant packages. Outside consumers should use Relay to
 * fetch the data (Relay will manage caching across products). This API can change and break
 * external consumers.
 */
export const useAgentsPaginatedStoreActions: HookActionsFunction<
	BoundActions<
		{
			/*
    Hash map which is the source of truth for agent data
    agent categories simply hold the agent ids to perform a lookup
    */
			agentsMap: Record<string, ClientAgent>;
			loadingCategories: Set<BrowseCategory>;
			agentCategories: StoreAgentCategories;
		},
		{
			/**
			 * This action populates a specific category with backend data
			 * it will store the ids in `agentCategories` and the agents in `agentsMap`
			 */
			populateAgentCategories: ({
				agentsList,
				pageInfo,
				category,
			}: {
				agentsList: Agent[];
				pageInfo: PageInfo;
				category: BrowseCategory;
			}) => ({
				setState,
				getState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => void;
			setAgentCategoriesByCategory: ({
				categoryName,
				agentCategoryData,
			}: {
				categoryName: BrowseCategory;
				agentCategoryData: StoreAgentCategory;
			}) => ({
				getState,
				setState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => void;
			setCategoryError: (
				categoryName: BrowseCategory,
				agentsError: Error | undefined,
			) => ({
				getState,
				setState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => void;
			getAgentCategoriesByCategory: (category: BrowseCategory) => ({
				getState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => StoreAgentCategory;
			removeAgent: (id: string) => ({
				setState,
				getState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => {
				agent: Agent | undefined;
				categories: BrowseCategory[];
			};
			addAgent: (
				agent: Agent,
				categories?: BrowseCategory[],
			) => ({
				setState,
				getState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => void;
			updateAgent: (
				id: string,
				data: Partial<Agent>,
			) => ({
				setState,
				getState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => Agent;
			setCategoryLoading: (
				category: BrowseCategory,
				loading: boolean,
			) => ({
				setState,
				getState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => void;
			abortCategoryFetch: (category: BrowseCategory) => ({
				getState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => void;

			setPlaceholderAgent: (
				agentId: string,
				data: Partial<Agent>,
			) => ({
				setState,
				getState,
			}: StoreActionApi<{
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => void;

			getCachedAgentByExternalConfigReference: (externalConfigReference: string) => ({
				getState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => Agent | undefined;
		}
	>
> = createActionsHook(agentsPaginatedStore);

// exported for testing
/**
 * @private
 * @deprecated Internal to conversation-assistant packages. Outside consumers should use Relay to
 * fetch the data (Relay will manage caching across products). This API can change and break
 * external consumers.
 */
export const useAgentsPaginatedByAgentIdStoreHook: HookFunction<
	{
		agent: Agent;
		doesAgentExist: boolean;
		isPlaceholder: boolean;
	},
	BoundActions<
		{
			/*
    Hash map which is the source of truth for agent data
    agent categories simply hold the agent ids to perform a lookup
    */
			agentsMap: Record<string, ClientAgent>;
			loadingCategories: Set<BrowseCategory>;
			agentCategories: StoreAgentCategories;
		},
		{
			/**
			 * This action populates a specific category with backend data
			 * it will store the ids in `agentCategories` and the agents in `agentsMap`
			 */
			populateAgentCategories: ({
				agentsList,
				pageInfo,
				category,
			}: {
				agentsList: Agent[];
				pageInfo: PageInfo;
				category: BrowseCategory;
			}) => ({
				setState,
				getState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => void;
			setAgentCategoriesByCategory: ({
				categoryName,
				agentCategoryData,
			}: {
				categoryName: BrowseCategory;
				agentCategoryData: StoreAgentCategory;
			}) => ({
				getState,
				setState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => void;
			setCategoryError: (
				categoryName: BrowseCategory,
				agentsError: Error | undefined,
			) => ({
				getState,
				setState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => void;
			getAgentCategoriesByCategory: (category: BrowseCategory) => ({
				getState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => StoreAgentCategory;
			removeAgent: (id: string) => ({
				setState,
				getState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => {
				agent: Agent | undefined;
				categories: BrowseCategory[];
			};
			addAgent: (
				agent: Agent,
				categories?: BrowseCategory[],
			) => ({
				setState,
				getState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => void;
			updateAgent: (
				id: string,
				data: Partial<Agent>,
			) => ({
				setState,
				getState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => Agent;
			setCategoryLoading: (
				category: BrowseCategory,
				loading: boolean,
			) => ({
				setState,
				getState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => void;
			abortCategoryFetch: (category: BrowseCategory) => ({
				getState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => void;

			setPlaceholderAgent: (
				agentId: string,
				data: Partial<Agent>,
			) => ({
				setState,
				getState,
			}: StoreActionApi<{
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => void;

			getCachedAgentByExternalConfigReference: (externalConfigReference: string) => ({
				getState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => Agent | undefined;
		}
	>,
	string
> = createHook(agentsPaginatedStore, {
	// TODO https://product-fabric.atlassian.net/browse/RAGE-618
	// change `{ agentId }` to `agentId`
	selector: (state, agentId: string) => ({
		agent: state.agentsMap[agentId],
		doesAgentExist: Boolean(state.agentsMap[agentId]),
		isPlaceholder: Boolean(state.agentsMap[agentId]?.isPlaceholder),
	}),
});

/**
 * @private
 * @deprecated Internal to conversation-assistant packages. Outside consumers should use Relay to
 * fetch the data (Relay will manage caching across products). This API can change and break
 * external consumers.
 */
export const useAgentsPaginatedByAgentId: (
	props: Omit<
		{
			agentId: string | undefined;
		} & AssistanceServiceInjectedParams,
		'assistanceServiceInjectedParams'
	> &
		AssistanceServiceInputParam,
) => {
	agent: Agent | undefined;
	error: ApiError | undefined;
} = createHookWithAssistanceService(
	({
		agentId,
		assistanceServiceInjectedParams: { assistanceService, experienceTracker, siteId },
	}: {
		agentId: string | undefined;
	} & AssistanceServiceInjectedParams) => {
		di(
			useAgentsPaginatedByAgentId,
			useAgentsPaginatedByAgentIdStoreHook,
			useAgentsPaginatedStoreActions,
		);

		const [{ agent, doesAgentExist, isPlaceholder }] = useAgentsPaginatedByAgentIdStoreHook(
			agentId ?? '',
		);
		const { updateAgent } = useAgentsPaginatedStoreActions();

		const { error } = useQuery({
			queryKey: fg('rovo_chat_site_switch_reset')
				? ['agentHydration', agentId ?? '', siteId ?? '']
				: ['agentHydration', agentId ?? ''],
			queryFn: async (): Promise<{ agentDetails: AgentDetails; generation: number }> => {
				if (!agentId) {
					// this throw won't happen because the `enabled` field below
					throw new Error('Agent ID is required');
				}

				const generation = getAgentStoresGeneration();

				experienceTracker.start({
					name: ExperienceName.HYDRATE_AGENT_DATA,
					id: ExperienceName.HYDRATE_AGENT_DATA,
				});
				return { agentDetails: await assistanceService.getAgentDetails(agentId), generation };
			},
			enabled: Boolean(agentId) && (!doesAgentExist || isPlaceholder),
			onSuccess: ({ agentDetails, generation }) => {
				if (!agentId) {
					return;
				}

				experienceTracker.succeed({
					name: ExperienceName.HYDRATE_AGENT_DATA,
				});

				if (fg('rovo_chat_site_switch_reset') && isStaleAgentStoresGeneration(generation)) {
					return;
				}

				updateAgent(agentId ?? '', agentDetails);
			},
			onError: (error) => {
				if (!agentId) {
					return;
				}

				const is404Error = error.errorAttributes.statusCode === 404;
				const is403Error =
					error.errorAttributes.statusCode === 403 && fg('convo-ai-enable-agents-use-permission');

				if (!(is404Error || is403Error)) {
					experienceTracker.fail({
						name: ExperienceName.HYDRATE_AGENT_DATA,
						error: error as Error,
						teamName: ROVO_AGENTS_FE_TEAM_NAME,
					});
				}
			},
		});

		if (!agentId) {
			return { agent: undefined, error: undefined };
		}

		return { agent, error: error ?? undefined };
	},
);

/**
 * @private
 * @deprecated This hook has few agent actions calling backend and does optimistic updates
 * Only to be used within this package as the optimistic update does not make sense for other packages
 */
export const useAgentsPaginatedActionsWithOptimistic: (
	props: Omit<UseAgentsControllerProps, 'assistanceServiceInjectedParams'> &
		AssistanceServiceInputParam,
) => {
	deleteAgent: {
		data: void | null;
		error: ApiError | null;
		isSubmitting: boolean;
		mutate: (
			payload: string,
			callbacks?:
				| {
						onSuccess?: ((data: void) => void) | undefined;
						onError?: (error: ApiError) => void;
				  }
				| undefined,
		) => Promise<void | undefined>;
	};
	setFavouriteAgent: (
		id: string,
		favourite: boolean,
		agentAri?: string | null,
	) => Promise<void | undefined>;
	setVerifiedAgent: (id: string, isVerified: boolean) => void;
	deleteAgentLocally: (id: string) => {
		restore: () => void;
	};
} = createHookWithAssistanceService(
	({ assistanceServiceInjectedParams }: UseAgentsControllerProps) => {
		const environment = useRelayEnvironment();
		const removedAgentCache = useRef<{
			categories: BrowseCategory[];
			agent: Agent | undefined;
			generation: number;
		}>({
			categories: [],
			agent: undefined,
			generation: 0,
		});

		const { removeAgent, addAgent, updateAgent } = useAgentsPaginatedStoreActions();
		const { favouriteAgent, unfavouriteAgent } = useAgentFavourites({
			assistanceServiceParams: assistanceServiceInjectedParams,
		});

		// Note: Experience Tracker events implemented in useDeleteAgent
		const { deleteAgent } = useDeleteAgent({
			assistanceServiceParams: assistanceServiceInjectedParams,
			onInit: (id) => {
				const { categories, agent } = removeAgent(id);
				// The generation is cached alongside the agent so the rollback below is judged against the
				// state this particular removal was made from.
				removedAgentCache.current = {
					categories,
					agent,
					generation: getAgentStoresGeneration(),
				};
			},
			onError: () => {
				// Restoring after a reset would put a complete agent from the previous site back into the
				// new site's map and its category lists.
				if (
					fg('rovo_chat_site_switch_reset') &&
					isStaleAgentStoresGeneration(removedAgentCache.current.generation)
				) {
					return;
				}

				if (removedAgentCache.current.agent) {
					addAgent(removedAgentCache.current.agent, removedAgentCache.current.categories);
				}
			},
		});

		// Note: Experience Tracker events implemented in useAgentFavourites
		const setFavouriteAgent = useCallback(
			async (id: string, favourite: boolean, agentAri?: string | null) => {
				const generationAtMutationStart = getAgentStoresGeneration();

				// Optimistic update
				updateAgent(id, { favourite });

				const localRelayUpdate = async (isFavourite: boolean) => {
					if (agentAri) {
						commitLocalUpdate(environment, (store) => {
							const id = RelayDataID(
								{
									id: agentAri,
								},
								'AgentStudioAssistant',
							);

							if (!id) {
								return;
							}

							const agentRecord = store.get(id);
							if (agentRecord) {
								agentRecord.setValue(isFavourite, 'isFavourite');
							}

							const root = store.getRoot();

							const connection = ConnectionHandler.getConnection(
								root,
								'paginatedAgentsList_agentStudio_getAgents',
								{
									cloudId: assistanceServiceInjectedParams.siteId,
									input: {
										onlyFavouriteAgents: true,
									},
									product: assistanceServiceInjectedParams.product,
								},
							);

							const agentDataID = agentRecord?.getDataID();

							if (!connection || !agentDataID || !agentRecord) {
								return;
							}

							if (isFavourite) {
								if (
									isExperimentEnabled('jira_agent_recommendations_m1') &&
									connectionContainsNode(connection, agentDataID)
								) {
									return;
								}

								const edge = ConnectionHandler.createEdge(
									store,
									connection,
									agentRecord,
									'AgentStudioAgentEdge',
								);

								ConnectionHandler.insertEdgeAfter(connection, edge);
							} else {
								ConnectionHandler.deleteNode(connection, agentDataID);
							}
						});
					}
				};

				localRelayUpdate(favourite);

				const errorCallback = {
					onError: () => {
						// Reverse optimistic update, unless the stores were reset for another site while the
						// mutation was in flight — `updateAgent` creates the entry when the id is absent, so
						// rolling back would insert a partial agent from the previous site into the new one.
						if (
							!(
								fg('rovo_chat_site_switch_reset') &&
								isStaleAgentStoresGeneration(generationAtMutationStart)
							)
						) {
							updateAgent(id, { favourite: !favourite });
						}

						// The Relay rollback is safe either way: its connection is keyed by cloudId.
						localRelayUpdate(!favourite);
					},
				};

				if (favourite) {
					return await favouriteAgent.mutate(id, errorCallback);
				} else {
					return await unfavouriteAgent.mutate(id, errorCallback);
				}
			},
			[
				assistanceServiceInjectedParams.product,
				assistanceServiceInjectedParams.siteId,
				environment,
				favouriteAgent,
				unfavouriteAgent,
				updateAgent,
			],
		);

		/**
		 * Function that updates the verified status of an agent.
		 * This can be removed once the dropdown has converted over to relay.
		 */
		const setVerifiedAgent = useCallback(
			(id: string, isVerified: boolean) => {
				updateAgent(id, { is_verified: isVerified });
			},
			[updateAgent],
		);

		// Local cache update function for optimistic delete
		// This only handles cache updates, not API calls or analytics
		const deleteAgentLocally = useCallback(
			(id: string) => {
				const generationAtRemoval = getAgentStoresGeneration();

				// Optimistically remove from cache
				const { categories, agent } = removeAgent(id);

				// Return restore function for error handling
				return {
					restore: () => {
						if (
							fg('rovo_chat_site_switch_reset') &&
							isStaleAgentStoresGeneration(generationAtRemoval)
						) {
							return;
						}

						if (agent) {
							addAgent(agent, categories);
						}
					},
				};
			},
			[removeAgent, addAgent],
		);

		return {
			deleteAgent,
			setFavouriteAgent,
			setVerifiedAgent,
			deleteAgentLocally,
		};
	},
);

/**
 * @private
 * @deprecated Internal to conversation-assistant packages. Outside consumers should use Relay to
 * fetch the data (Relay will manage caching across products). This API can change and break
 * external consumers.
 */
export const usePaginatedAgentsByCategory: HookFunction<
	{
		agentsByCategory: Agent[];
		isCategoryInitialized: boolean;
		isCategoryLoading: boolean;
		isCategoryEmpty: boolean;
		hasNextPage: boolean;
		nextPageCursor: string | null | undefined;
		agentsError: Error | undefined;
	},
	BoundActions<
		{
			/*
    Hash map which is the source of truth for agent data
    agent categories simply hold the agent ids to perform a lookup
    */
			agentsMap: Record<string, ClientAgent>;
			loadingCategories: Set<BrowseCategory>;
			agentCategories: StoreAgentCategories;
		},
		{
			/**
			 * This action populates a specific category with backend data
			 * it will store the ids in `agentCategories` and the agents in `agentsMap`
			 */
			populateAgentCategories: ({
				agentsList,
				pageInfo,
				category,
			}: {
				agentsList: Agent[];
				pageInfo: PageInfo;
				category: BrowseCategory;
			}) => ({
				setState,
				getState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => void;
			setAgentCategoriesByCategory: ({
				categoryName,
				agentCategoryData,
			}: {
				categoryName: BrowseCategory;
				agentCategoryData: StoreAgentCategory;
			}) => ({
				getState,
				setState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => void;
			setCategoryError: (
				categoryName: BrowseCategory,
				agentsError: Error | undefined,
			) => ({
				getState,
				setState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => void;
			getAgentCategoriesByCategory: (category: BrowseCategory) => ({
				getState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => StoreAgentCategory;
			removeAgent: (id: string) => ({
				setState,
				getState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => {
				agent: Agent | undefined;
				categories: BrowseCategory[];
			};
			addAgent: (
				agent: Agent,
				categories?: BrowseCategory[],
			) => ({
				setState,
				getState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => void;
			updateAgent: (
				id: string,
				data: Partial<Agent>,
			) => ({
				setState,
				getState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => Agent;
			setCategoryLoading: (
				category: BrowseCategory,
				loading: boolean,
			) => ({
				setState,
				getState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => void;
			abortCategoryFetch: (category: BrowseCategory) => ({
				getState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => void;

			setPlaceholderAgent: (
				agentId: string,
				data: Partial<Agent>,
			) => ({
				setState,
				getState,
			}: StoreActionApi<{
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => void;

			getCachedAgentByExternalConfigReference: (externalConfigReference: string) => ({
				getState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => Agent | undefined;
		}
	>,
	BrowseCategory
> = createHook(agentsPaginatedStore, {
	selector: (state, category: BrowseCategory) => ({
		agentsByCategory: state.agentCategories[category].agentIds.map(
			(agentId) => state.agentsMap[agentId],
		),

		isCategoryInitialized:
			state.agentCategories[category].pageInfo.startCursor !== undefined &&
			state.agentCategories[category].pageInfo.endCursor !== undefined,
		isCategoryLoading: state.loadingCategories.has(category),
		isCategoryEmpty: state.agentCategories[category].agentIds.length === 0,

		hasNextPage: state.agentCategories[category].pageInfo.hasNextPage,
		nextPageCursor: state.agentCategories[category].pageInfo.endCursor,

		agentsError: state.agentCategories[category].agentsError,
	}),
});

// exported for testing only
/**
 * @private
 * @deprecated Internal to conversation-assistant packages. Outside consumers should use Relay to
 * fetch the data (Relay will manage caching across products). This API can change and break
 * external consumers.
 */
export const usePaginatedAgentsWithOverrides: (
	props: Omit<UseAgentsControllerProps, 'assistanceServiceInjectedParams'> &
		AssistanceServiceInputParam,
) => {
	fetchOverriddenAgentsAndPopulateStore: () => Promise<string[] | null | undefined>;
} = createHookWithAssistanceService(
	({
		assistanceServiceInjectedParams: { assistanceService, experienceTracker, siteId },
	}: UseAgentsControllerProps) => {
		di(useAgentOverrider, useAgentsPaginatedStoreActions);
		const { agentDefaultIdOverride, agentIdFiltersOverride } = useAgentOverrider();
		const { addAgent, populateAgentCategories } = useAgentsPaginatedStoreActions();

		const fetchOverriddenAgentsAndPopulateStoreQuery = useQuery({
			queryKey: fg('rovo_chat_site_switch_reset')
				? [
						'fetchOverriddenAgentsAndPopulateStore',
						agentDefaultIdOverride ?? '',
						agentIdFiltersOverride?.join(',') ?? '',
						siteId ?? '',
					]
				: [
						'fetchOverriddenAgentsAndPopulateStore',
						agentDefaultIdOverride ?? '',
						agentIdFiltersOverride?.join(',') ?? '',
					],
			queryFn: async () => {
				const isSiteSwitchResetEnabled = fg('rovo_chat_site_switch_reset');
				const generationAtRequestStart = getAgentStoresGeneration();

				const agentIdsToFetchSet: Set<string> = new Set();
				if (agentDefaultIdOverride) {
					agentIdsToFetchSet.add(agentDefaultIdOverride);
				}
				if (agentIdFiltersOverride?.length) {
					agentIdFiltersOverride.forEach((agentId) => {
						agentIdsToFetchSet.add(agentId);
					});
				}

				if (agentIdsToFetchSet.size === 0) {
					return;
				}
				const agentIdsToFetch = Array.from(agentIdsToFetchSet) as string[];

				populateAgentCategories({
					category: 'all-agents',
					agentsList: [],
					pageInfo: DEFAULT_PAGE_INFO,
				});

				experienceTracker.start({
					name: ExperienceName.FETCH_AGENTS_OVERRIDES,
					id: ExperienceName.FETCH_AGENTS_OVERRIDES,
				});

				const agents = await Promise.all(
					agentIdsToFetch.map(async (agentId) => {
						return await assistanceService.getAgentDetails(agentId);
					}),
				);

				if (isSiteSwitchResetEnabled && isStaleAgentStoresGeneration(generationAtRequestStart)) {
					return agentIdsToFetch;
				}

				// then add the agents into "All agents" category
				agents.forEach((agent) => {
					addAgent(agent, ['all-agents']);
				});

				return agentIdsToFetch;
			},
			onSuccess: () => {
				experienceTracker.succeed({
					name: ExperienceName.FETCH_AGENTS_OVERRIDES,
				});
			},
			onError: (error) => {
				experienceTracker.fail({
					name: ExperienceName.FETCH_AGENTS_OVERRIDES,
					error: error as Error,
					teamName: ROVO_AGENTS_FE_TEAM_NAME,
				});
			},
			enabled: false,
		});

		return {
			fetchOverriddenAgentsAndPopulateStore: fetchOverriddenAgentsAndPopulateStoreQuery.refetch,
		};
	},
);

/**
 * @private
 * @deprecated Internal to conversation-assistant packages. Outside consumers should use Relay to
 * fetch the data (Relay will manage caching across products). This API can change and break
 * external consumers.
 */
export const usePaginatedAgentsActions: (
	props: Omit<UseAgentsControllerProps, 'assistanceServiceInjectedParams'> &
		AssistanceServiceInputParam,
) => {
	fetchAgentsByCategory: ({
		category,
		after,
		query,
	}: {
		category?: BrowseCategory;
		after?: string | null;
		query?: string;
	}) => Promise<string[]>;
	populateAgentCategories: (args_0: {
		agentsList: Agent[];
		pageInfo: PageInfo;
		category: BrowseCategory;
	}) => void;
	setAgentCategoriesByCategory: (args_0: {
		categoryName: BrowseCategory;
		agentCategoryData: StoreAgentCategory;
	}) => void;
	setCategoryError: (categoryName: BrowseCategory, agentsError: Error | undefined) => void;
	getAgentCategoriesByCategory: (category: BrowseCategory) => StoreAgentCategory;
	removeAgent: (id: string) => {
		agent: Agent | undefined;
		categories: BrowseCategory[];
	};
	addAgent: (agent: Agent, categories?: BrowseCategory[] | undefined) => void;
	updateAgent: (id: string, data: Partial<Agent>) => Agent;
	setCategoryLoading: (category: BrowseCategory, loading: boolean) => void;
	abortCategoryFetch: (category: BrowseCategory) => void;
	setPlaceholderAgent: (agentId: string, data: Partial<Agent>) => void;
	getCachedAgentByExternalConfigReference: (externalConfigReference: string) => Agent | undefined;
} = createHookWithAssistanceService(
	({ onError, assistanceServiceInjectedParams }: UseAgentsControllerProps) => {
		di(useAgentsPaginatedStoreActions, useAgentOverrider, usePaginatedAgentsWithOverrides);

		const {
			experienceTracker,
			siteId,
			product,
			headers: assistanceServiceHeaders,
		} = assistanceServiceInjectedParams;

		const actions = useAgentsPaginatedStoreActions();
		const {
			setCategoryLoading,
			setCategoryError,
			populateAgentCategories,
			getAgentCategoriesByCategory,
			setAgentCategoriesByCategory,
		} = actions;

		const { agentDefaultIdOverride, agentIdFiltersOverride } = useAgentOverrider();
		const { fetchOverriddenAgentsAndPopulateStore } = usePaginatedAgentsWithOverrides({
			assistanceServiceParams: assistanceServiceInjectedParams,
		});

		const fetchAgentsByCategory = useCallback(
			async ({
				category = 'all-agents',
				after,
				query,
			}: {
				category?: BrowseCategory;
				after?: string | null;
				query?: string;
			}): Promise<string[]> => {
				// if there are any overrides, cancel the normal fetchByCategory and instead fetch the overridden agents
				if (agentDefaultIdOverride || agentIdFiltersOverride?.length) {
					return (await fetchOverriddenAgentsAndPopulateStore()) ?? [];
				}

				const isSiteSwitchResetEnabled = fg('rovo_chat_site_switch_reset');
				const generationAtRequestStart = getAgentStoresGeneration();

				const dataBeforeFetch = getAgentCategoriesByCategory(category);

				experienceTracker.start({
					name: ExperienceName.FETCH_AGENTS_PAGINATED,
					id: ExperienceName.FETCH_AGENTS_PAGINATED,
				});

				let queryInput: {
					onlyTemplateAgents?: boolean;
					onlyFavouriteAgents?: boolean;
					onlyMyAgents?: boolean;
					onlyVerifiedAgents?: boolean;
					name?: string;
				} = {};

				switch (category) {
					case 'starred-agents':
						queryInput.onlyFavouriteAgents = true;
						break;
					case 'my-agents':
						queryInput.onlyMyAgents = true;
						break;
					case 'templates':
						queryInput.onlyTemplateAgents = true;
						break;
					case 'verified-agents':
						queryInput.onlyVerifiedAgents = true;
						break;
				}

				if (query) {
					queryInput.name = query;
				}

				// for rolling back data in case of abort/failure
				const resetData = (): string[] => {
					setAgentCategoriesByCategory({
						categoryName: category,
						agentCategoryData: dataBeforeFetch,
					});

					return dataBeforeFetch.agentIds;
				};

				// No after means we're requesting the first page so the category data is reset
				if (!after) {
					populateAgentCategories({
						category,
						agentsList: [],
						pageInfo: DEFAULT_PAGE_INFO,
					});
				}

				setCategoryLoading(category, true);
				setCategoryError(category, undefined);

				const headers = new Headers(assistanceServiceHeaders);
				const maybeWorkspaceId = headers.get('x-workspace-id');

				try {
					const response = await fetchPaginatedAgents({
						after: after ?? undefined,
						cloudId: siteId,
						count: 10,
						input: Object.keys(queryInput).length > 0 ? queryInput : undefined,
						product: product,
						abortController: getAgentCategoriesByCategory(category).abortController,
						workspaceId: maybeWorkspaceId,
					});

					if (isSiteSwitchResetEnabled && isStaleAgentStoresGeneration(generationAtRequestStart)) {
						experienceTracker.abort({
							reason: 'Aborted',
							name: ExperienceName.FETCH_AGENTS_PAGINATED,
						});
						return [];
					}

					setCategoryLoading(category, false);

					if (getAgentCategoriesByCategory(category).abortController.signal?.aborted) {
						experienceTracker.abort({
							reason: 'Aborted',
							name: ExperienceName.FETCH_AGENTS_PAGINATED,
						});
						return resetData();
					}

					if (response) {
						populateAgentCategories({
							category,
							agentsList: response.agents,
							pageInfo: response.pageInfo,
						});

						experienceTracker.succeed({
							name: ExperienceName.FETCH_AGENTS_PAGINATED,
						});
					}

					return response?.agents.map((agent) => agent.id);
				} catch (error) {
					if (isSiteSwitchResetEnabled && isStaleAgentStoresGeneration(generationAtRequestStart)) {
						experienceTracker.abort({
							reason: 'Aborted',
							name: ExperienceName.FETCH_AGENTS_PAGINATED,
						});
						return [];
					}

					experienceTracker.fail({
						name: ExperienceName.FETCH_AGENTS_PAGINATED,
						error: error as Error,
						teamName: ROVO_AGENTS_FE_TEAM_NAME,
					});

					const prevAgentIds = resetData();
					setCategoryError(category, error as Error);
					setCategoryLoading(category, false);

					if (error instanceof ApiError) {
						onError?.(error);
					}

					return prevAgentIds;
				}
			},
			[
				agentDefaultIdOverride,
				agentIdFiltersOverride?.length,
				getAgentCategoriesByCategory,
				experienceTracker,
				setCategoryLoading,
				setCategoryError,
				assistanceServiceHeaders,
				fetchOverriddenAgentsAndPopulateStore,
				setAgentCategoriesByCategory,
				populateAgentCategories,
				siteId,
				product,
				onError,
			],
		);

		return {
			...actions,
			fetchAgentsByCategory,
		};
	},
);

// exported for testing only
/**
 * @private
 * @deprecated Internal to conversation-assistant packages. Outside consumers should use Relay to
 * fetch the data (Relay will manage caching across products). This API can change and break
 * external consumers.
 */
export const usePaginatedAgentsMap: HookFunction<
	Record<string, ClientAgent>,
	BoundActions<
		{
			/*
    Hash map which is the source of truth for agent data
    agent categories simply hold the agent ids to perform a lookup
    */
			agentsMap: Record<string, ClientAgent>;
			loadingCategories: Set<BrowseCategory>;
			agentCategories: StoreAgentCategories;
		},
		{
			/**
			 * This action populates a specific category with backend data
			 * it will store the ids in `agentCategories` and the agents in `agentsMap`
			 */
			populateAgentCategories: ({
				agentsList,
				pageInfo,
				category,
			}: {
				agentsList: Agent[];
				pageInfo: PageInfo;
				category: BrowseCategory;
			}) => ({
				setState,
				getState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => void;
			setAgentCategoriesByCategory: ({
				categoryName,
				agentCategoryData,
			}: {
				categoryName: BrowseCategory;
				agentCategoryData: StoreAgentCategory;
			}) => ({
				getState,
				setState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => void;
			setCategoryError: (
				categoryName: BrowseCategory,
				agentsError: Error | undefined,
			) => ({
				getState,
				setState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => void;
			getAgentCategoriesByCategory: (category: BrowseCategory) => ({
				getState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => StoreAgentCategory;
			removeAgent: (id: string) => ({
				setState,
				getState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => {
				agent: Agent | undefined;
				categories: BrowseCategory[];
			};
			addAgent: (
				agent: Agent,
				categories?: BrowseCategory[],
			) => ({
				setState,
				getState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => void;
			updateAgent: (
				id: string,
				data: Partial<Agent>,
			) => ({
				setState,
				getState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => Agent;
			setCategoryLoading: (
				category: BrowseCategory,
				loading: boolean,
			) => ({
				setState,
				getState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => void;
			abortCategoryFetch: (category: BrowseCategory) => ({
				getState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => void;
			setPlaceholderAgent: (
				agentId: string,
				data: Partial<Agent>,
			) => ({
				setState,
				getState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => void;
			getCachedAgentByExternalConfigReference: (externalConfigReference: string) => ({
				getState,
			}: StoreActionApi<{
				/*
        Hash map which is the source of truth for agent data
        agent categories simply hold the agent ids to perform a lookup
        */
				agentsMap: Record<string, ClientAgent>;
				loadingCategories: Set<BrowseCategory>;
				agentCategories: StoreAgentCategories;
			}>) => Agent | undefined;
		}
	>,
	void
> = createHook(agentsPaginatedStore, {
	selector: (state) => state.agentsMap,
});

const DEFAULT_RECOMMENDED_AGENTS_LIMIT = 3;

type RecommendedAgentsResponse = Awaited<
	ReturnType<
		AssistanceServiceInjectedParams['assistanceServiceInjectedParams']['assistanceService']['getRecommendedAgents']
	>
>;

const getRecommendedAgentsQueryKey = ({
	limit,
	pageUrl,
	siteId,
}: {
	limit: number;
	pageUrl?: string;
	siteId?: string;
}) =>
	fg('rovo_chat_site_switch_reset')
		? ['recommendedAgents', limit, pageUrl ?? '', siteId ?? '']
		: ['recommendedAgents', limit, pageUrl ?? ''];

const fetchRecommendedAgents = async ({
	assistanceService,
	limit,
	pageUrl,
	updateAgent,
}: {
	assistanceService: AssistanceServiceInjectedParams['assistanceServiceInjectedParams']['assistanceService'];
	limit: number;
	pageUrl?: string;
	updateAgent: (id: string, agent: Agent) => void;
}): Promise<RecommendedAgentsResponse> => {
	const isSiteSwitchResetEnabled = fg('rovo_chat_site_switch_reset');
	const generationAtRequestStart = getAgentStoresGeneration();

	const response = await assistanceService.getRecommendedAgents({
		limit,
		pageUrl,
	});

	if (isSiteSwitchResetEnabled && isStaleAgentStoresGeneration(generationAtRequestStart)) {
		return response;
	}

	// Store the agents in the paginated store for optimistic updates etc.
	// Not adding them to a specific category in local cache on purpose.
	response.agents.forEach((agent) => {
		updateAgent(agent.id, agent);
	});

	return response;
};

/**
 * @private
 * @deprecated Internal to conversation-assistant packages. Outside consumers should use Relay to
 * fetch the data (Relay will manage caching across products). This API can change and break
 * external consumers.
 */
export const prefetchRecommendedAgents = ({
	assistanceService,
	limit = DEFAULT_RECOMMENDED_AGENTS_LIMIT,
	pageUrl,
	updateAgent,
	onStart,
	onSuccess,
	onError,
}: {
	assistanceService: AssistanceServiceInjectedParams['assistanceServiceInjectedParams']['assistanceService'];
	limit?: number;
	pageUrl?: string;
	updateAgent: (id: string, agent: Agent) => void;
	onStart?: () => void;
	onSuccess?: (response: RecommendedAgentsResponse) => void;
	onError?: (error: ApiError) => void;
}): void => {
	prefetchQuery({
		queryKey: getRecommendedAgentsQueryKey({
			limit,
			pageUrl,
			siteId: assistanceService.getConfig().siteId,
		}),
		queryFn: () =>
			fetchRecommendedAgents({
				assistanceService,
				limit,
				pageUrl,
				updateAgent,
			}),
		onStart,
		onSuccess,
		onError,
	});
};

/**
 * @private
 * @deprecated Internal to conversation-assistant packages. Outside consumers should use Relay to
 * fetch the data (Relay will manage caching across products). This API can change and break
 * external consumers.
 */
export const useRecommendedAgents: (
	props: Omit<
		AssistanceServiceInjectedParams & {
			limit?: number;
			pageUrl?: string;
		},
		'assistanceServiceInjectedParams'
	> &
		AssistanceServiceInputParam,
) => {
	isFetching: boolean;
	recommendedAgents: Agent[];
	recommendationKey?: string | undefined;
} = createHookWithAssistanceService(
	({
		assistanceServiceInjectedParams,
		limit = DEFAULT_RECOMMENDED_AGENTS_LIMIT,
		pageUrl,
	}: AssistanceServiceInjectedParams & {
		limit?: number;
		pageUrl?: string;
	}): {
		isFetching: boolean;
		recommendedAgents: Agent[];
		recommendationKey?: string | undefined;
	} => {
		di(usePaginatedAgentsMap);
		const { assistanceService, experienceTracker, siteId } = assistanceServiceInjectedParams;
		const [agentsMap, { updateAgent }] = usePaginatedAgentsMap();

		const { isFetching, data } = useQuery({
			queryKey: getRecommendedAgentsQueryKey({ limit, pageUrl, siteId }),
			queryFn: async () => {
				experienceTracker.start({
					id: ExperienceName.GET_RECOMMENDED_AGENTS,
					name: ExperienceName.GET_RECOMMENDED_AGENTS,
				});

				return fetchRecommendedAgents({
					assistanceService,
					limit,
					pageUrl,
					updateAgent,
				});
			},
			enabled: Boolean(siteId),
			onSuccess: () => {
				experienceTracker.succeed({
					name: ExperienceName.GET_RECOMMENDED_AGENTS,
				});
			},
			onError: (error) => {
				if (isHipaaError(error)) {
					experienceTracker.succeed({
						name: ExperienceName.GET_RECOMMENDED_AGENTS,
					});
					return;
				}
				experienceTracker.fail({
					name: ExperienceName.GET_RECOMMENDED_AGENTS,
					error,
					teamName: ROVO_AGENTS_FE_TEAM_NAME,
				});
			},
		});

		return {
			isFetching,
			recommendedAgents: data?.agents.map((agent) => agentsMap[agent.id]).filter(Boolean) ?? [],
			recommendationKey: data?.metadata?.recommendationKey,
		};
	},
);

/**
 * @private
 * @deprecated Internal to conversation-assistant packages. Outside consumers should use Relay to
 * fetch the data (Relay will manage caching across products). This API can change and break
 * external consumers.
 */
export const usePaginatedAgentByConfigReference: (
	props: Omit<UseAgentsControllerProps, 'assistanceServiceInjectedParams'> &
		AssistanceServiceInputParam,
) => {
	getAndStoreAgentByExternalConfigReference: ({
		externalConfigReference,
	}: {
		externalConfigReference: string;
	}) => Promise<Agent | undefined>;
} = createHookWithAssistanceService(
	({
		assistanceServiceInjectedParams: { experienceTracker, siteId },
	}: UseAgentsControllerProps) => {
		di(useAgentsPaginatedStoreActions, fetchAgentByExternalConfigReference);

		const { updateAgent, getCachedAgentByExternalConfigReference } =
			useAgentsPaginatedStoreActions();

		const getAndStoreAgentByExternalConfigReference = useCallback(
			async ({ externalConfigReference }: { externalConfigReference: string }) => {
				const cachedAgent = getCachedAgentByExternalConfigReference(externalConfigReference);
				if (cachedAgent) {
					return cachedAgent;
				}

				const isSiteSwitchResetEnabled = fg('rovo_chat_site_switch_reset');
				const generationAtRequestStart = getAgentStoresGeneration();

				try {
					experienceTracker.start({
						name: ExperienceName.FETCH_AGENT_BY_EXTERNAL_CONFIG_REFERENCE,
						id: ExperienceName.FETCH_AGENT_BY_EXTERNAL_CONFIG_REFERENCE,
					});

					const agentData = await fetchAgentByExternalConfigReference({
						cloudId: siteId,
						externalConfigReference,
					});
					if (!agentData) {
						if (!fg('rovo_agents_external_reference_lookup_killswitch')) {
							experienceTracker.succeed({
								name: ExperienceName.FETCH_AGENT_BY_EXTERNAL_CONFIG_REFERENCE,
							});
							return undefined;
						}
						throw new Error('Agent lookup returned no data');
					}

					if (
						!(isSiteSwitchResetEnabled && isStaleAgentStoresGeneration(generationAtRequestStart))
					) {
						// cache the data
						updateAgent(agentData.id, agentData);
					}

					experienceTracker.succeed({
						name: ExperienceName.FETCH_AGENT_BY_EXTERNAL_CONFIG_REFERENCE,
					});

					return agentData;
				} catch (error) {
					experienceTracker.fail({
						name: ExperienceName.FETCH_AGENT_BY_EXTERNAL_CONFIG_REFERENCE,
						error: error as Error,
						teamName: ROVO_AGENTS_FE_TEAM_NAME,
					});

					return undefined;
				}
			},
			[getCachedAgentByExternalConfigReference, experienceTracker, siteId, updateAgent],
		);

		return {
			getAndStoreAgentByExternalConfigReference,
		};
	},
);

/**
 * @private
 * @deprecated Internal to conversation-assistant packages. Outside consumers should use Relay to
 * fetch the data (Relay will manage caching across products). This API can change and break
 * external consumers.
 * Within this package, use `usePaginatedAgents...` hooks instead.
 * @TODO move to different folder https://product-fabric.atlassian.net/browse/RAGE-1562
 */
export const useAgentsController: typeof useAgentsControllerInternal = (param) => {
	return useAgentsControllerInternal(param);
};

/**
 * Use useAgentsList in contexts in which we want to display at least three default favourited agents, such as in the editor
 * @private
 * @deprecated Internal to conversation-assistant packages. Outside consumers should use Relay to
 * fetch the data (Relay will manage caching across products). This API can change and break
 * external consumers.
 * Within this package, use `usePaginatedAgents...` hooks instead.
 * @TODO move to different folder https://product-fabric.atlassian.net/browse/RAGE-1562
 */
export const useAgentsList: (
	props: Omit<UseAgentsListProps, 'assistanceServiceInjectedParams'> & AssistanceServiceInputParam,
) => Agent[] = createHookWithAssistanceService(
	({
		assistanceServiceInjectedParams: { product, experienceId, headers, touchpointSource, siteId },
		filter,
	}: UseAgentsListProps) => {
		const { availableAgents, favouritedAgents } = useAgentsController({
			assistanceServiceParams: {
				product,
				experienceId,
				headers,
				touchpointSource,
				siteId,
			},
		});

		const defaultAgents = getSuggestedOotbAgents({ availableAgents, product });

		const defaultAgentsOutsideUserFavourites = defaultAgents.filter(
			(defaultAgent) => !favouritedAgents.find((favAgent) => favAgent.id === defaultAgent.id),
		);

		const favouritedAgentsWithDefault = [
			...favouritedAgents,
			...defaultAgentsOutsideUserFavourites.slice(0, Math.max(3 - favouritedAgents.length, 0)),
		];

		return filter === 'favourites' ? favouritedAgentsWithDefault : availableAgents;
	},
);
